import React, { useEffect, useState } from "react";
import { X, Trash } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { fetchAuth } from "../common/api";

const surfaces = [
  { label: "Natural dry", value: "field1" },
  { label: "Natural wet", value: "field2" },
  { label: "Synthetic dry", value: "field3" },
  { label: "Synthetic wet", value: "field4" },
];

const UploadModal = ({ isOpen, onClose, club_id, addSimulation }) => {
  const [selectedSurface, setSelectedSurface] = useState(surfaces[0].value);
  const [filesToSimulate, setFilesToSimulate] = useState([]);
  const [apiActivities, setApiActivities] = useState([]);
  const [isLoadingApi, setIsLoadingApi] = useState(false);
  const [currentTab, setCurrentTab] = useState("api");//manual or api
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // 🚫 Club bloqué pour les simulations
  const BLOCKED_CLUB_ID = "7c61d7a5-7da7-409a-9bfd-18416640f640";
  const ONLY_MANUAL_CLUB_ID = "79495f3c-b7f7-41b4-ad77-9f9b4a50e2d6"; // Club ID for which only manual uploads are allowed
  const isBlockedClub = club_id === BLOCKED_CLUB_ID;

  const resetState = () => {
    setFilesToSimulate([]);
    setApiActivities([]);
    setIsLoadingApi(false);
    if (ONLY_MANUAL_CLUB_ID === club_id) {
      setCurrentTab("manual");
    }else {
      setCurrentTab("api");
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (isOpen) {
      resetState();
      fetchApiActivities();
    }
  }, [isOpen]);

  const fetchApiActivities = async () => {
    setIsLoadingApi(true);
    try {
      const res = await fetchAuth(`/gps_activities/detect_new`);
      const data = await res.json();
      if (res.ok) setApiActivities(data);
    } catch (err) {
     
    } finally {
      setIsLoadingApi(false);
    }
  };

  const validateFile = async (file) => {
    const formData = new FormData();
    formData.append("files", file);
    formData.append("club_id", club_id);
    try {
      const response = await fetchAuth(`/check-gps-files`, {
        method: "POST",
        body: formData,
        headers: {},
      });
      const result = await response.json();
      if (response.ok && result.results) {
        return result.results
          .filter((f) => f.valid)
          .map((f) => ({
            ...f,
            file: file 
          }));
      }
    } catch (err) {
    
    }
    return [];
  };

  const onDrop = async (acceptedFiles) => {
    const results = await Promise.all(acceptedFiles.map(validateFile));
    const newValid = results.flat();
    const unique = newValid.filter(
      (newF) => !filesToSimulate.some(
        (exist) =>
          exist.first_name === newF.first_name &&
          exist.last_name === newF.last_name &&
          exist.date === newF.date &&
          exist.hour === newF.hour &&
          exist.period === newF.period
      )
    );
    setFilesToSimulate([...filesToSimulate, ...unique]);
  };

  const handleDeleteFile = (index) => {
    const updated = [...filesToSimulate];
    updated.splice(index, 1);
    setFilesToSimulate(updated);
  };

  const handleDeleteActivity = (index) => {
    const updated = [...apiActivities];
    updated.splice(index, 1);
    setApiActivities(updated);
  };

  const handleSubmitManual = async () => {
    if (filesToSimulate.length === 0) return;
    setIsSubmitting(true);
    filesToSimulate.forEach((fileData) => {
      addSimulation(fileData.player_id, fileData.file, selectedSurface);
    });
    resetState();
    onClose();
  };

  const handleSubmitApi = async () => {
    if (apiActivities.length === 0) return;
    setIsSubmitting(true);
    apiActivities.forEach((activity) => {
      addSimulation(activity.player_id, activity.activityId, selectedSurface,true); // 👈 Utilise `id` au lieu de `file`
    });
    resetState();
    onClose();
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    },
  });

  return (
    <div
      className={`z-50 fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 ${isOpen ? "flex" : "hidden"}`}
    >
      {isLoadingApi ? (
        <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-[800px] relative max-h-[30vh] overflow-hidden">
          <div className="text-center py-10 text-black">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
            Loading new activities...
          </div>
        </div>
      ) : (
        <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-[800px] relative max-h-[90vh] overflow-auto">
          <button
            onClick={() => {
              onClose();
              resetState();
            }}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>

          {/* 🔁 Onglets */}
          <div className="flex space-x-4 mb-4">
            <button
              className={`font-bold text-black ${currentTab === "api" ? "underline" : ""}`}
              disabled={isLoadingApi}
              onClick={() => setCurrentTab("api")}
            >
              🔁 New Activities (API)
            </button>
            <button
              className={`font-bold text-black ${currentTab === "manual" ? "underline" : ""}`}
              disabled={true}
              onClick={() => setCurrentTab("manual")}
              style={{ display: "none" }}
            >
              📁 Manual Import
            </button>
          </div>

          {/* ✅ Scrollable container */}
          <div className="overflow-y-auto max-h-[50vh] border rounded-md p-2">
            {currentTab === "api" && (
              <>
                {apiActivities.length > 0 ? (
                  <table className="w-full text-black border">
                    <thead>
                      <tr className="bg-gray-200">
                        <th className="p-2">Player</th>
                        <th>Date</th>
                        <th>Hour</th>
                        <th>Period</th>
                        <th>Type</th>
                        <th>🗑️</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apiActivities.map((a, idx) => (
                        <tr key={idx} className="text-center border">
                          <td className="p-2">{a.player}</td>
                          <td>{a.date}</td>
                          <td>{a.hour}</td>
                          <td>{a.period}</td>
                          <td>{a.type}</td>
                          <td>
                            <button onClick={() => handleDeleteActivity(idx)}>
                              <Trash size={18} className="text-red-500 hover:text-red-700" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-black">No new activities detected.</p>
                )}
              </>
            )}

            {currentTab === "manual" && (
              <>
                <div
                  {...getRootProps()}
                  className="border-2 border-dashed border-gray-300 p-6 text-center cursor-pointer hover:bg-gray-100 mb-4"
                >
                  <input {...getInputProps()} />
                  <p className="text-black">Drag and drop your files here</p>
                  <p className="text-sm text-gray-600">or click to explore</p>
                </div>

                {filesToSimulate.length > 0 && (
                  <table className="w-full text-black border">
                    <thead>
                      <tr className="bg-gray-200">
                        <th className="p-2">Player</th>
                        <th>Date</th>
                        <th>Hour</th>
                        <th>Period</th>
                        <th>🗑️</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filesToSimulate.map((f, idx) => (
                        <tr key={idx} className="text-center">
                          <td>{f.first_name} {f.last_name}</td>
                          <td>{f.date}</td>
                          <td>{f.hour}</td>
                          <td>{f.period}</td>
                          <td>
                            <button onClick={() => handleDeleteFile(idx)}>
                              <Trash size={18} className="text-red-500 hover:text-red-700" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>

          {/* Surface Dropdown */}
          <div className="mt-4" style={{ display: "none" }}>
            <label className="text-black font-semibold">🌱 Type of surface :</label>
            <select
              className="w-full text-black p-2 mt-2 border border-gray-300 rounded-lg"
              value={selectedSurface}
              onChange={(e) => setSelectedSurface(e.target.value)}
            >
              {surfaces.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* 🚫 Message pour club bloqué */}
          {isBlockedClub && (
            <div className="mt-4 p-3 bg-red-50 border border-red-300 rounded-lg text-red-800 text-sm">
              ⚠️ Simulations are temporarily disabled for your club. Please contact support for more information.
            </div>
          )}

          {/* ✅ Bouton de validation */}
          <button
            onClick={currentTab === "manual" ? handleSubmitManual : handleSubmitApi}
            disabled={isBlockedClub || isSubmitting || (currentTab === "manual" ? filesToSimulate.length === 0 : apiActivities.length === 0)}
            className={`mt-4 w-full py-2 rounded-lg font-bold transition ${
              isBlockedClub || isSubmitting ? "bg-gray-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            {isSubmitting ? "⏳ Sending..." : isBlockedClub ? "🚫 Simulations disabled" : "✅ Validate"}
          </button>
        </div>
      )}
    </div>
  );
};

export default UploadModal;
